interface ListPagerProps {
  /** 1-based page being shown; clamped into range here. */
  page: number;
  pageSize: number;
  /** Items across ALL pages — never just the page's own slice. */
  total: number;
  onPageChange: (page: number) => void;
  /** Plural noun for the count line, e.g. "cameras". */
  itemLabel?: string;
  className?: string;
}

/**
 * The bar under a client-side paginated list — the camera grid, tree and table on both the
 * local and cloud Camera List use this one, so the bar looks and sits the same everywhere.
 * The count always shows (even on a single page); the page controls only when there is
 * somewhere to go.
 */
export default function ListPager({ page, pageSize, total, onPageChange, itemLabel = 'items', className = '' }: ListPagerProps) {
  if (total <= 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  const pages: (number | string)[] = [];
  const delta = 2;
  const left = Math.max(1, current - delta);
  const right = Math.min(totalPages, current + delta);
  if (left > 1) pages.push(1);
  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages - 1) pages.push('...');
  if (right < totalPages) pages.push(totalPages);

  const navBtn = 'rounded-md px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors border border-border';

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-2 pt-3 border-t border-border ${className}`}>
      <p className="text-[12px] text-muted-foreground">
        Page <span className="font-semibold text-foreground">{current}</span> of <span className="font-semibold text-foreground">{totalPages}</span> • Showing {start}–{end} of {total} {itemLabel}
      </p>
      {totalPages > 1 && (
        <nav className="flex items-center gap-1">
          <button onClick={() => onPageChange(current - 1)} disabled={current <= 1} className={navBtn}>← Prev</button>
          {pages.map((p, i) =>
            p === '...' ? (
              <span key={`e${i}`} className="px-1.5 text-muted-foreground text-[12px]">…</span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p as number)}
                aria-current={p === current ? 'page' : undefined}
                className={`rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
                  p === current ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {p}
              </button>
            )
          )}
          <button onClick={() => onPageChange(current + 1)} disabled={current >= totalPages} className={navBtn}>Next →</button>
        </nav>
      )}
    </div>
  );
}
